import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import {
  remixButtonIcon,
  MoreVerticalIcon,
  favoriteImg,
  SavedContent,
  profileImageSmall,
  placeholderImageVertical,
  placeholderImageHorizontal,
  createUniverse,
  photoMarker
} from 'shared-ui/assets'
import {
  MobileTabBar,
  Text,
  useToast,
  LazyImage,
  GuestUserModal,
  BottomSheetList,
  RemixBottomSheet,
  SmartImage,
  BackButton,
  ShareModal,
  LikeListBottomDrawer,
  CustomDiscoverCardShimmer,
  BlurImage
} from 'shared-ui/components'
import { formatApiError } from 'shared-ui/api'
import { followUser } from 'shared-ui/redux/actions/faceSwapAction'
import { openConsentModal } from 'shared-ui/redux/reducers/consentReducer'
import { MINI_APP_IDS } from 'shared-ui/utils/constants/config'
import {ImageListWrapper,ImageListContainer} from './style.jsx'
import {
  useLikeMutation,
  useUnlikeMutation,
  useUnfollowMutation,
  useFollowMutation,
  useResponsive,
  useLikesByContentId,
  useInfiniteCommentsByContentId,
  usePostComment,
  usePopularTools,
  useShare,
  useSaveContent,
  useDiscoveDeleteContent,
  useScrollLock,
  useCustomShare
} from 'shared-ui/hooks'
import { media } from 'shared-ui/utils/media'
import { truncateText, getTimeAgo } from 'shared-ui/utils/helper'
import { getGuestUser } from 'shared-ui/utils/localStorage'
import { updateGenerationSession } from 'shared-ui/redux/reducers/generationSessionReducer'
import {
  CONTENT_TYPE,
  PAGE_LABELS,
  EROS_LABEL,
  ERROR_MESSAGES,
  POPUP_MESSAGE,
  SUPER_APP_CONFIG,
  QUERY_KEY,
  SUCCESS_MESSAGE
} from 'shared-ui/utils/constants'
import {
  FeedContainer,
  FeedStatIcon,
  FeedRemixButton,
} from '../Discover/style'
import ActionSheet from '../Discover/OptionSheet'
import DesktopMoreOptions from '../Discover/DesktopMoreOptions'
import ReportSheet from '../Discover/ReportSheet'
import { GA_EVENTS } from 'shared-ui/utils/gaConstants'
import { useTrackEvent } from 'shared-ui/utils/analyticEvents'
import { useQueryClient } from '@tanstack/react-query'

const ImageList = ({
  listData,
  initialContentId,
  parentLocation,
  isUGC,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  loadMoreRef,
  queryKey,
  fromDashboard,
  category,
  initialLoading
}) => {
  const isMobile = useResponsive(media.mobile)
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToast()
  const { isOpen, shareDataDetails, openShare, closeShare, openShareModal } = useCustomShare()
  const location = useLocation()
  const trackEvent = useTrackEvent()
  const navListData = location.state?.listData
  const contentOverridesRef = useRef({})

  const fromNotYetPublished = location.state?.fromNotYetPublished
  const isFromDashboard = location.state?.fromDashboard || fromDashboard
  const queryClient = useQueryClient()
  const user = useSelector((state) => state.auth.user)
  const currentUserId = user?.user_id
  const currentUserName = user?.name
  const currentUserProfilePic = user?.image
  const [followedUsers, setFollowedUsers] = useState({})
  const [imageData, setImageData] = useState([])
  const [showLikesSheet, setShowLikesSheet] = useState(false)
  const [clikedContent, setClickedContent] = useState(null)
  const [selectedPost, setSelectedPost] = useState(null)
  const [likesActionUserId, setLikesActionUserId] = useState(null)
  const [showGuestModal, setShowGuestModal] = useState(false)
  const [likesFollowState, setLikesFollowState] = useState({})
  const [showCommentsSheet, setShowCommentsSheet] = useState(false)
  const [commentInput, setCommentInput] = useState('')
  const [clickedComment, setClickedComment] = useState(null)
  const [showOptionsSheet, setShowOptionsSheet] = useState(false)
  const [reportedIds, setReportedIds] = useState([]);
  const [isReportOpen, setReportOpen] = useState(false)
  const [reportReason, setReportReason] = useState('')
  const [reportText, setReportText] = useState('')
  const [showRemixSheet, setShowRemixSheet] = useState(false)
  const [openDropdownId, setOpenDropdownId] = useState(null)

  const contentRefs = useRef(new Map())
  const moreBtnRef = useRef(null)
  const optionsJustOpenedAtRef = useRef(0)
  const isOptionsTransitioningRef = useRef(false)

  const { mutate: likeMutate, isLoading: likeLoading } = useLikeMutation(isUGC)

  const { mutate: unlikeMutate, isLoading: unlikeLoading } =
    useUnlikeMutation(isUGC)
  const unfollowMutation = useUnfollowMutation(isUGC)
  const followMutation = useFollowMutation(isUGC)

  const {
    data: likesList,
    isLoading: likesLoading,
    refetch: refetchLikesList
  } = useLikesByContentId(clikedContent, isUGC, {
    enabled: showLikesSheet
  })
  const {
    data: infiniteCommentsData,
    fetchNextPage: fetchNextCommentsPage,
    isLoading: commentsLoading,
    hasNextPage: hasMoreComments,
    isFetchingNextPage: isFetchingMoreComments,
    refetch: refetchCommentList
  } = useInfiniteCommentsByContentId(clickedComment, isUGC, {
    enabled: showCommentsSheet
  })
  const checkGuestUser = () => {
    if (getGuestUser() !== 'true') return false;
    setShowGuestModal(true);
    return true;
  };

  const consentMiniApps = useSelector((state) => state.consent?.miniApps) || {}
  const isConsentModalOpen = useSelector((state) => state.consent?.modal?.isOpen)
  const pendingRemixPostRef = useRef(null)
  const createConsent = consentMiniApps[MINI_APP_IDS.CREATE]

  useEffect(() => {
    if (pendingRemixPostRef.current && createConsent?.hasAgreed && createConsent?.isOn) {
      setSelectedPost(pendingRemixPostRef.current)
      setShowRemixSheet(true)
      pendingRemixPostRef.current = null
    }
  }, [createConsent?.hasAgreed, createConsent?.isOn])

  useEffect(() => {
    if (!isConsentModalOpen && (!createConsent?.hasAgreed || !createConsent?.isOn)) {
      pendingRemixPostRef.current = null
    }
  }, [isConsentModalOpen, createConsent?.hasAgreed, createConsent?.isOn])

  const handleRemixClick = (e, post) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (checkGuestUser()) return

    const createConsent = consentMiniApps[MINI_APP_IDS.CREATE]
    if (!createConsent?.hasAgreed || !createConsent?.isOn) {
      pendingRemixPostRef.current = post
      dispatch(
        openConsentModal({
          miniAppId: MINI_APP_IDS.CREATE,
          isModalBlocking: false
        })
      )
      return
    }

    setSelectedPost(post)
    setShowRemixSheet(true)
  }
  const commentRefetchTimeout = useRef(null);
  const scrollTimeoutRef = useRef();
  const commentsList = useMemo(() => {
    return infiniteCommentsData?.pages?.flatMap((page) => page.comments) || []
  }, [infiniteCommentsData])
  const { mutateAsync: postComment,isPending:commentPending } = usePostComment(isUGC)

  const {
    data: popularToolsData = { data: [] },
    isLoading: popularToolsLoading
  } = usePopularTools({ enabled: showRemixSheet })

  useEffect(() => {
    if (!imageData?.length) return

    const initialState = {}
    imageData.forEach((post) => {
      if (!post || !post.user_id) return
      initialState[post.user_id] = post.is_following || false
    })

    setFollowedUsers(initialState)
  }, [imageData])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreBtnRef.current && !moreBtnRef.current.contains(event.target)) {
        setOpenDropdownId(null)
      }
    }

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdownId])

  useEffect(() => {
    if (!listData?.length) return

    const applyOverrides = (items) => {
      const overrides = contentOverridesRef.current || {}
      if (!items?.length) return items
      if (!Object.keys(overrides).length) return items
      return items.map((item) => {
        const id = item?.content_id ?? item?.id
        if (!id) return item
        const patch = overrides[id]
        if (!patch) return item

        const updatedPatch = { ...patch }
        let hasRemainingOverrides = false

        Object.keys(patch).forEach((key) => {
          // If server data has caught up with our local change, remove it from overrides
          if (item[key] === patch[key]) {
            delete updatedPatch[key]
          } else {
            hasRemainingOverrides = true
          }
        })

        if (!hasRemainingOverrides) {
          delete contentOverridesRef.current[id]
          return item
        }

        // Update the ref to store only the still-relevant overrides
        contentOverridesRef.current[id] = updatedPatch
        return { ...item, ...updatedPatch }
      })
    }

    // const processedListData = listData
    const processedListData = fromNotYetPublished
      ? listData.filter((item) => (item?.moderation_status_output === 'approved' || item?.moderation_status_output === null))
      : listData

    if (!initialContentId) {
      setImageData(applyOverrides(processedListData))
      return
    }

    const index = processedListData?.findIndex(
      (v) => v?.id === initialContentId || v?.content_id === initialContentId
    )

    if (index === -1) {
      setImageData(applyOverrides(processedListData))
      return
    }

    const newOrder = [
      processedListData[index],
      ...processedListData.slice(0, index),
      ...processedListData.slice(index + 1)
    ]
    setImageData(applyOverrides(newOrder))
  }, [listData, initialContentId, fromNotYetPublished])

  const { mutate: shareMutate } = useShare({
    title: EROS_LABEL,
    text: PAGE_LABELS.checkShare,
    isUGC: isUGC,
    onSuccess: (response,id) => {
      openShare({
        title: response?.title,
        text: response?.description,
        url: response?.share_link || response?.content_file_url,
        isUGC: isUGC,
        id,
        key:queryKey
      })
    },
    onError: (error) => {
      showError(formatApiError(error))
    }
  })

  const { mutateAsync: saveContentMutate, isLoading: saveLoading } =
    useSaveContent(user?.user_id, {
      onSuccess: () => {
        showSuccess(SUCCESS_MESSAGE?.SAVED_TO_DASHBOARD || 'Saved to My Dashboard')
      },
      onError: (err) => {
        showError(formatApiError(err))
      }
    })

  const { mutateAsync: deleteMutation, isLoading: deleteLoading } = useDiscoveDeleteContent({
    onSuccess: () => {
      // deletion handled optimistically
    },
    onError: (err) => {
      showError(formatApiError(err))
    }
  })

  const isAnySheetOpen =
    showLikesSheet ||
    showCommentsSheet ||
    showOptionsSheet ||
    showRemixSheet ||
    isReportOpen ||
    showGuestModal

  useScrollLock(Boolean(isAnySheetOpen))

  useEffect(() => {
    if (!selectedPost) return
    const isUGC = selectedPost?.is_ugc
    if (isUGC) return

    if (selectedPost?.is_ugc !== null && !selectedPost?.is_ugc) {
      const template_id =
        (selectedPost?.content_type || '').toLowerCase() === CONTENT_TYPE.VIDEO
          ? selectedPost?.template_id || selectedPost?.video_template_id || selectedPost?.id || selectedPost?.content_id
          : selectedPost?.template_id || selectedPost?.image_template_id || selectedPost?.id || selectedPost?.content_id
      dispatch(
        updateGenerationSession({
          template_id,
          content_type: (selectedPost?.content_type || '').toLowerCase(),
          thumbnail_url: selectedPost?.thumbnail || selectedPost?.file_url,
          file_url: selectedPost?.file_url
        })
      )
    }
  }, [selectedPost, dispatch])

  const handleProfileClick = (id) => {
    if (checkGuestUser()) return;
    if (id === user?.user_id) {
      navigate('/profile')
    } else {
      navigate(`/creator/${id}`)
    }
  }

  const renderCommentItem = (comment) => {
    return (
      <div key={comment.id} className="flex gap-3 py-3">
        <div className=" w-10 h-10 shrink-0">
          <LazyImage
            src={comment?.profile_picture || profileImageSmall}
            backUpImage={profileImageSmall}
            alt={comment?.username}
            className="w-full h-full rounded-full object-cover shrink-0 cursor-pointer"
            onClick={() => handleProfileClick(comment.user_id)}
          />
        </div>

        <div className="w-[296px] flex flex-col gap-2 ">
          <div
            className="text-[#999999] text-xs font-semibold text-left cursor-pointer"
            onClick={() => handleProfileClick(comment.user_id)}
          >
            {comment?.username}
          </div>
          <div
            className=" text-sm font-normal text-left break-words max-h-full"
            style={{ color: theme.app.iconlabel }}
          >
            {comment?.comment_text}
          </div>
        </div>
      </div>
    )
  }
  const handleLike = (content_id, user_id) => {
    if (checkGuestUser()) return;
    const postIndex = imageData?.findIndex(
      (p) => (p.content_id || p.id) === content_id
    )
    if (postIndex === -1) return
    const post = imageData[postIndex]

    // Optimistic Update
    const isLiking = !post.liked
    const newLiked = isLiking
    const newLikesCount = isLiking
      ? (post.likes_count || 0) + 1
      : Math.max(0, (post.likes_count || 0) - 1)

    const updatedImageData = [...imageData]
    updatedImageData[postIndex] = {
      ...post,
      liked: newLiked,
      likes_count: newLikesCount
    }
    setImageData(updatedImageData)

    const mutationOptions = {
      onSuccess: () => {
        // optimistic update already done; no extra refetch needed
      },
      onError: () => {
        // Rollback on error
        setImageData((prev) => {
          const rollbackData = [...prev]
          rollbackData[postIndex] = post
          return rollbackData
        })
      },
    }

    if (post.liked) {
      unlikeMutate(content_id, mutationOptions)
    } else {
      trackEvent(GA_EVENTS.content_liked, {
        content_id: content_id,
        creator_id: user_id
      })
      likeMutate(content_id, mutationOptions)
    }
  }

  const handleLikesSheetFollowToggle = (like) => {
    if (checkGuestUser()) return;
    if (!user?.user_id) {
      showError(POPUP_MESSAGE.DISCOVER_FOLLOW_ERROR)
      return
    }

    const targetUserId = like?.user_id || like?.id
    if (!targetUserId) {
      showError(POPUP_MESSAGE.DISCOVER_UPDATE_FAILED_CURRENT)
      return
    }

    const currentFollowStatus =
      likesFollowState[targetUserId] ?? like?.is_following ?? false

    setLikesFollowState((prev) => ({
      ...prev,
      [targetUserId]: !currentFollowStatus
    }))
    setLikesActionUserId(targetUserId)

    const payload = { user_id: user?.user_id, follower_id: targetUserId }
    const mutationOptions = {
      onSuccess: (resp) => {
        if (typeof refetchLikesList === 'function') {
          refetchLikesList()
        }
        // showSuccess(resp?.message || (currentFollowStatus ? SUCCESS_MESSAGE.UNFOLLOWED : SUCCESS_MESSAGE.FOLLOWED))
      },
      onError: (err) => {
        showError(err?.message || POPUP_MESSAGE.DISCOVER_UPDATE_FAILED)
        setLikesFollowState((prev) => ({
          ...prev,
          [targetUserId]: currentFollowStatus
        }))
      },
      onSettled: () => {
        setLikesActionUserId(null)
        if (queryKey) {
          queryClient.invalidateQueries({ queryKey: [queryKey] })
        }
      }
    }

    if (currentFollowStatus) {
      unfollowMutation.mutate(payload, mutationOptions)
    } else {
      trackEvent(GA_EVENTS.creator_followed, {
        creator_id: targetUserId?.user_id,
        source: 'home'
      })
      followMutation.mutate(payload, mutationOptions)
    }
    queryClient.invalidateQueries(QUERY_KEY.GetCoinBalance)
    queryClient.invalidateQueries(QUERY_KEY.GetCreditBalance)
  }


  const handleFollow = async (cardUserId) => {
    const isCurrentlyFollowing = followedUsers[cardUserId] || false

    setFollowedUsers((prev) => ({
      ...prev,
      [cardUserId]: !isCurrentlyFollowing
    }))

    try {
      if (isCurrentlyFollowing) {
        await unfollowMutation.mutateAsync({
          user_id: user?.user_id,
          follower_id: cardUserId
        })
      } else {
        await dispatch(
          followUser({
            user_id: user?.user_id,
            follower_id: cardUserId
          })
        )
      }
    } catch (err) {
      // rollback if API fails
      setFollowedUsers((prev) => ({
        ...prev,
        [cardUserId]: isCurrentlyFollowing
      }))
    }

    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.GetCoinBalance] })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.GetCreditBalance] })

    if (queryKey) {
      queryClient.invalidateQueries({ queryKey: [queryKey] })
    }
  }

  const handleSendComment = async () => {
    if (!user?.user_id || !clickedComment || !commentInput.trim()) return

    trackEvent(GA_EVENTS.comment_posted, {
      content_id: clickedComment,
      creator_id: user?.user_id,
      comment_length: commentInput.length
    })

    const postIndex = imageData.findIndex(
      (p) => (p.content_id || p.id) === clickedComment
    )

    if (postIndex === -1) return

    const originalPost = imageData[postIndex]

    // ✅ optimistic update (ONLY ONCE)
    setImageData((prev) =>
      prev.map((post) =>
        (post.content_id || post.id) === clickedComment
          ? {
            ...post,
            comments_count: (post.comments_count || 0) + 1
          }
          : post
      )
    )

    try {
      await postComment(
        {
          user_id: user.user_id,
          content_id: clickedComment,
          comment_text: commentInput.trim(),
          isUGC
        },
        {
          onSuccess: () => {
            refetchCommentList()
            clearTimeout(commentRefetchTimeout.current);

            commentRefetchTimeout.current = setTimeout(() => {
              refetchCommentList();
            }, 2000)

            if (queryKey) {
              queryClient.invalidateQueries({
                queryKey: [queryKey],
                exact: false
              })
            }

            queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.GetCoinBalance]
            })

            queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.GetCreditBalance]
            })
          },

          onError: () => {
            // rollback optimistic update
            setImageData((prev) => {
              const rollback = [...prev]
              rollback[postIndex] = originalPost
              return rollback
            })
          }
        }
      )

      setCommentInput('')
    } catch (e) {
      showError('Failed to post comment')
    }
  }

  const handleShare = (id, user_id) => {
    if (checkGuestUser()) return;
    openShareModal()
    shareMutate(id) //trigger the mutation manually
    trackEvent(GA_EVENTS.content_shared, {
      content_id: id,
      creator_id: user_id,
      platform: ''
    })

  }

  const handleSave = async (content_id, user_id) => {
    trackEvent(GA_EVENTS.content_saved, {
      content_id: content_id,
      creator_id: user_id
    })
    if (checkGuestUser()) return;
    if (saveLoading || deleteLoading) return
    if (!user?.user_id || !content_id) return

    try {
      await saveContentMutate(content_id, {
        onSuccess: () => {
          contentOverridesRef.current[content_id] = {
            ...(contentOverridesRef.current[content_id] || {}),
            is_saved: true
          }

          setImageData((prevImageData) => {
            const postIndex = prevImageData?.findIndex(
              (p) => (p.content_id || p.id) === content_id
            )
            if (postIndex === -1) return prevImageData

            const post = prevImageData[postIndex]
            const updatedPost = { ...post, is_saved: true }
            const nextImageData = [...prevImageData]
            nextImageData[postIndex] = updatedPost

            setSelectedPost((prevSelected) => {
              if (
                prevSelected &&
                (prevSelected.content_id || prevSelected.id) ===
                (post.content_id || post.id)
              ) {
                return updatedPost
              }
              return prevSelected
            })

            return nextImageData
          })
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.GetCoinBalance]
          })
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.GetCreditBalance]
          })
          if (queryKey) {
            queryClient.invalidateQueries({ queryKey: [queryKey] })
          }
        },
        onError: (err) => {
          try {
            const parsed =
              typeof err?.message === 'string'
                ? JSON.parse(err.message)
                : err?.message
            showError(
              parsed?.message || parsed || ERROR_MESSAGES.FAILED_TO_SAVE
            )
          } catch (e) {
            showError(err?.message || ERROR_MESSAGES.FAILED_TO_SAVE)
          }
        }
      })
    } catch (e) {
      /* error already handled in onError */
    }
  }

  const handleUnSave = async (content_id) => {
    if (checkGuestUser()) return;
    if (saveLoading || deleteLoading) return
    if (!user?.user_id || !content_id) return

    try {
      await deleteMutation(content_id, {
        onSuccess: () => {
          contentOverridesRef.current[content_id] = {
            ...(contentOverridesRef.current[content_id] || {}),
            is_saved: false
          }

          setImageData((prevImageData) => {
            const postIndex = prevImageData?.findIndex(
              (p) => (p.content_id || p.id) === content_id
            )
            if (postIndex === -1) return prevImageData

            const post = prevImageData[postIndex]
            const updatedPost = { ...post, is_saved: false }
            const updatedImageData = [...prevImageData]
            updatedImageData[postIndex] = updatedPost

            setSelectedPost((prevSelected) => {
              if (
                prevSelected &&
                (prevSelected.content_id || prevSelected.id) ===
                (post.content_id || post.id)
              ) {
                return updatedPost
              }
              return prevSelected
            })

            return updatedImageData
          })
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.GetCoinBalance]
          })
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.GetCreditBalance]
          })
          if (queryKey) {
            queryClient.invalidateQueries({ queryKey: [queryKey] })
          }
        },
        onError: (err) => {
          showError(err?.message || ERROR_MESSAGES.FAILED_TO_REMOVE_SAVES)
        }
      })
    } catch (error) {
      // Error occurred
    }
  }

  const handleToggleSave = (is_saved, content_id, user_id) => {
    if (checkGuestUser()) return;
    if (is_saved) handleUnSave(content_id)
    else handleSave(content_id, user_id)
  }

  useLayoutEffect(() => {
    const savedScroll = location.state?.scrollPosition
    const clickedContentId = location.state?.clickedContentId
    const dataSync = location.state?.dataSync
    if (dataSync?.id != null) {
      contentOverridesRef.current = {
        ...(contentOverridesRef.current || {}),
        [dataSync.id]: {
          liked: dataSync.liked,
          is_liked: dataSync.liked,
          likes_count: dataSync.likes_count,
          comments_count:
            dataSync.comments_count != null
              ? dataSync.comments_count
              : existing.comments_count,
          title: dataSync.title,
          is_saved: dataSync.is_saved,
          is_following: dataSync.is_following
        }
      }
      setImageData((prev) => {
        let base =
          Array.isArray(prev) && prev.length
            ? prev
            : listData?.length
              ? listData
              : navListData || []

        if (fromNotYetPublished) {
          base = base.filter((item) => (item?.moderation_status_output === 'approved' || item?.moderation_status_output === null))
        }

        const idx = base.findIndex(
          (p) => (p?.content_id || p?.id) === dataSync.id
        )
        if (idx === -1) return base
        const existing = base[idx]
        const next = [...base]
        next[idx] = {
          ...existing,
          liked: dataSync.liked,
          // Some APIs/components use different naming; keep both in sync.
          is_liked: dataSync.liked,
          likes_count:
            dataSync.likes_count != null
              ? dataSync.likes_count
              : existing.likes_count,
          comments_count:
            dataSync.comments_count != null
              ? dataSync.comments_count
              : existing.comments_count,
          title: dataSync.title,
          is_saved: dataSync.is_saved,
          is_following: dataSync.is_following
        }
        return next
      })

      const creatorId = location.state?.creatorId

      if (creatorId != null) {
        // Update overrides for ALL posts from this creator
        const updatedOverrides = { ...contentOverridesRef.current }

        let base = imageData?.length
          ? imageData
          : listData?.length
            ? listData
            : navListData || []

        if (fromNotYetPublished) {
          base = base.filter((item) => (item?.moderation_status_output === 'approved' || item?.moderation_status_output === null))
        }

        base.forEach((item) => {
          if (item?.user_id === creatorId) {
            const id = item?.content_id || item?.id
            if (!id) return

            updatedOverrides[id] = {
              ...(updatedOverrides[id] || {}),
              is_following: dataSync.is_following
            }
          }
        })

        contentOverridesRef.current = updatedOverrides

        // update UI immediately
        setImageData((prev) =>
          prev.map((item) =>
            item?.user_id === creatorId
              ? { ...item, is_following: dataSync.is_following }
              : item
          )
        )
      }
    }

    if (savedScroll == null && clickedContentId == null) return

    if (clickedContentId) {
      const element = contentRefs.current.get(clickedContentId)
      if (element) {
        scrollTimeoutRef.current = setTimeout(() => {
          element.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
        }, 100);
        return
      }
    }

    if (savedScroll != null) {
      let attempts = 0
      const restoreScroll = () => {
        if (Math.abs(window.scrollY - savedScroll) < 5 || attempts > 20) {
          return
        }
        window.scrollTo({ top: savedScroll, behavior: 'auto' })
        attempts++
        requestAnimationFrame(restoreScroll)
      }
      requestAnimationFrame(restoreScroll)
    }
     return () => {
    clearTimeout(scrollTimeoutRef.current);
  };
  }, [location.state, listData])

  const handleContentClick = (id, is_ugc) => {
    trackEvent(GA_EVENTS.content_opened, {
      content_id: id,
      source:
        (location.pathname.toLowerCase().includes('creator') &&
          'top-creator') ||
        location?.state.contentGAsource ||
        parentLocation
    })

    navigate(`/content/${id}`, {
      state: {
        prevLocation: location.pathname,
        listData: imageData,
        scrollPosition: window.scrollY,
        parentLocation: parentLocation,
        fromNotYetPublished: fromNotYetPublished,
        fromDashboard: isFromDashboard,
        contentGAsource: location?.state.contentGAsource,
        is_UGC_state: isUGC || is_ugc,
        queryKey: queryKey || '',
        category: category || ''
      }
    })
  }
  const handleBackButton = () => {
    if (location.state?.dataSync) {
      navigate(-2)
    } else if (location.state?.parentLocation) {
      navigate(location.state.parentLocation, { replace: true })
    } else {
      navigate(-1)
    }
  }

  const fetchingRef = useRef(false)

  useEffect(() => {
    return () => {
      clearTimeout(commentRefetchTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (!hasNextPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !fetchingRef.current
        ) {
          fetchingRef.current = true

          fetchNextPage().finally(() => {
            fetchingRef.current = false
          })
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    )

    const el = loadMoreRef.current
    if (el) observer.observe(el)

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <ImageListWrapper $isMobile={isMobile}>
      <FeedContainer
        style={{
          width: isMobile ? '100%' : '560px',
          margin: 'auto'
        }}
      >
        <ImageListContainer>
          <BackButton onBackButtonClick={handleBackButton} />
        </ImageListContainer>
        <div className="flex flex-col gap-8">
          {imageData &&
            imageData?.filter(Boolean).map((post) => {
              if (
                !post ||
                (post?.content_type || '').toLowerCase() === CONTENT_TYPE.VIDEO
              )
                return null
              const isFollowing = followedUsers[post.user_id] || false
              const parent = parentLocation?.split('/')[1]
              const isContentReported = reportedIds?.includes(post?.content_id || post?.id)
              return (
                <div
                  className="flex flex-col gap-2"
                  key={post.content_id || post.id}
                >
                  {/* {!fromNotYetPublished && ( */}
                 {!isContentReported &&<div className="flex justify-between items-center">
                    <div
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => {
                        if (post.user_id === currentUserId) {
                          navigate('/profile')
                        } else {
                          navigate(`/creator/${post.user_id}`)
                        }
                        trackEvent(GA_EVENTS.creator_profile_viewed, {
                          creator_id: post.user_id,
                          source: 'home'
                        })
                      }}
                    >
                      <div className="w-8 h-8">
                        <img
                          className="w-full h-full object-cover rounded-full"
                          src={
                            fromNotYetPublished
                              ? currentUserProfilePic
                              : post.user_info?.profile_picture ||
                                profileImageSmall ||
                                currentUserProfilePic
                            // `https://i.pravatar.cc/100?u=${post.user_id}`
                          }
                          alt=""
                          onError={(e) => {
                            e.target.src = profileImageSmall
                          }}
                        />
                      </div>

                      <div
                        className="flex flex-col items-start"
                        style={{ color: theme.app.text }}
                      >
                        <div>
                          {fromNotYetPublished
                            ? currentUserName
                            : post?.username ||
                              post?.user_info?.username ||
                              currentUserName}
                        </div>
                        <div className="text-xs" style={{ color: '#999999' }}>
                          {getTimeAgo(post.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-10">
                      {post?.user_id !== user?.user_id && (
                        <div
                          onClick={() => {
                            if (checkGuestUser()) return;
                            handleFollow(post.user_id)
                          }}
                          style={{
                            color: '#00bfff',
                            cursor: 'pointer'
                          }}
                        >
                          {isFollowing
                            ? SUPER_APP_CONFIG?.DISCOVER_INDEX_FOLLOWING
                            : SUPER_APP_CONFIG?.DISCOVER_INDEX_FOLLOW}
                        </div>
                      )}

                      {!fromNotYetPublished && (
                        <div className="cursor-pointer relative">
                          <img
                            src={MoreVerticalIcon}
                            alt="MoreVertical"
                            ref={
                              openDropdownId === (post.content_id || post.id)
                                ? moreBtnRef
                                : null
                            }
                            onClick={(e) => {
                             if (checkGuestUser()) return;
                              e.stopPropagation()
                              if (isMobile) {
                                if (
                                  isOptionsTransitioningRef.current ||
                                  isAnySheetOpen
                                )
                                  return
                                if (
                                  Date.now() - optionsJustOpenedAtRef.current <
                                  200
                                )
                                  return

                                isOptionsTransitioningRef.current = true
                                optionsJustOpenedAtRef.current = Date.now()
                                setSelectedPost(post)
                                setShowOptionsSheet(true)
                                isOptionsTransitioningRef.current = false
                              } else {
                                setOpenDropdownId(
                                  openDropdownId ===
                                    (post.content_id || post.id)
                                    ? null
                                    : post.content_id || post.id
                                )
                              }
                            }}
                          />
                          {!isMobile &&
                            openDropdownId === (post.content_id || post.id) && (
                              <DesktopMoreOptions
                                data={post}
                                user={user}
                                hideSave={false}
                                onSaveToggle={handleToggleSave}
                                onClose={() => setOpenDropdownId(null)}
                                onReport={() => {
                                  setSelectedPost(post)
                                  setReportOpen(true)
                                }}
                                onAbout={() => {
                                  if (post.user_id === currentUserId) {
                                    navigate('/profile')
                                  } else {
                                    navigate(`/creator/${post.user_id}`)
                                  }
                                }}
                                desktopMenuOpen={true}
                              />
                            )}
                        </div>
                      )}
                    </div>
                  </div>}
                  {/* )
                  } */}
                  <div
                    className="relative w-full min-h-24 cursor-pointer"
                    onClick={() =>
                      handleContentClick(
                        post?.content_id || post?.id,
                        post?.is_ugc
                      )
                    }
                    ref={(el) => {
                      if (el)
                        contentRefs.current.set(post.id || post.content_id, el)
                      else
                        contentRefs.current.delete(post.id || post.content_id)
                    }}
                  >
                    <>
                    <SmartImage
                      src={post.thumbnail}
                      alt={post.caption}
                      orientation={post.orientation}
                      placeholderPortrait={placeholderImageVertical}
                      placeholderLandscape={placeholderImageHorizontal}
                      imgClassName="shadow-md"
                      className="h-full w-full cursor-pointer rounded-2xl"
                      imgStyle={{ maxHeight: '720px' }}
                      style={{ border: `1px solid ${theme.app.imageBorder}` }}
                    />
                    <div className="absolute top-0 left-0 h-full w-full p-4 flex flex-col justify-between">
                      <div className="self-end">
                        <LazyImage height={24} width={24} src={photoMarker} />
                      </div>
                      <div className="flex w-full justify-between items-center">
                        <div className="flex flex-col items-start font-normal">
                          <div
                            className="text-base"
                            style={{ color: theme.app.text }}
                          >
                            {truncateText(post?.title, 25)}
                          </div>
                          {post?.subtitle && (
                            <div
                              className="text-xs"
                              style={{ color: theme.app.iconlabel }}
                            >
                              {truncateText(post?.tags, 25)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </>
                    
                    {isContentReported && <BlurImage handleShowContent={() => setReportedIds((prev) =>
                      prev.filter((id) => id !== post.content_id && id !== post.id)
                    )} />}
                  </div>
                 
                  
                  {!isContentReported &&<div className="flex justify-between items-center">
                    {!fromNotYetPublished && (
                      <div className="flex gap-5 items-center">
                        <div className="flex items-center gap-1">
                          <div
                            style={{
                              cursor:
                                likeLoading || unlikeLoading
                                  ? 'not-allowed'
                                  : 'pointer',
                              filter: post.liked ? undefined : undefined
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              !(likeLoading || unlikeLoading) &&
                                handleLike(
                                  post.content_id || post.id,
                                  post.user_id
                                )
                              if (checkGuestUser()) return;
                              setClickedContent(post.content_id || post.id)
                            }}
                          >
                            <FeedStatIcon
                              src={
                                post.liked ? favoriteImg : theme?.app?.likeIcon
                              }
                              alt={post.liked ? 'FavoriteIcon' : 'LikeIcon'}
                              isfavorite={post.liked}
                            />
                          </div>
                          <div
                            className="cursor-pointer text-xs"
                            style={{ color: theme?.app?.iconlabel }}
                            onClick={() => {
                              if (checkGuestUser()) return;
                              setShowLikesSheet(true)
                              setClickedContent(post.content_id || post.id)
                            }}
                          >
                            {post.likes_count || 0}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            onClick={() => {
                              if (checkGuestUser()) return;
                              setClickedComment(post.content_id || post.id)
                              setShowCommentsSheet(true)
                            }}
                            className="cursor-pointer"
                          >
                            <FeedStatIcon
                              src={theme?.app?.commentIcon}
                              alt="CommentIcon"
                            />
                          </div>
                          <div
                            className="cursor-pointer text-xs"
                            onClick={() => {
                              if (checkGuestUser()) return;
                              setClickedComment(post.content_id || post.id)
                              setShowCommentsSheet(true)
                            }}
                            style={{ color: theme?.app?.iconlabel }}
                          >
                            {post.comments_count || 0}
                          </div>
                        </div>
                        <div
                          className="flex items-center gap-1 cursor-pointer"
                          onClick={() => {
                           if (checkGuestUser()) return;
                            handleShare(
                              post.content_id || post.id,
                              post.user_id
                            )
                          }}
                        >
                          <FeedStatIcon
                            src={theme?.app?.shareIcon}
                            alt="shareIcon"
                          />
                          <div
                            className="cursor-pointer text-xs"
                            style={{ color: theme?.app?.iconlabel }}
                          >
                            {post?.share_count || 0}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="cursor-pointer">
                            <FeedStatIcon
                              src={
                                post.is_saved
                                  ? SavedContent
                                  : theme?.app?.bookmarkIcon
                              }
                              alt="Bookmark"
                              isSaved={post.is_saved}
                              onClick={() => {
                                if (checkGuestUser()) return;
                                handleToggleSave(
                                  post.is_saved,
                                  post.content_id || post.id,
                                  post.user_id
                                )
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {!fromNotYetPublished && (
                      <div
                        onClick={(e) => handleRemixClick(e, post)}
                      >
                        {post.is_ugc ||
                        post.is_ugc === null ||
                        (followedUsers[post.user_id] &&
                          parent !== 'explore-gallery' &&
                          parent !== 'home') ? (
                          // followedUsers?.includes(post?.user_id) ? (
                          <button
                            className="flex items-center justify-center rounded-full border transition-all focus:outline-none"
                            style={{
                              width: '50px',
                              height: '40px',
                              border: '1px solid #00B8F8',
                              background: 'transparent'
                            }}
                            onClick={(e) => handleRemixClick(e, post)}
                          >
                            <LazyImage
                              src={remixButtonIcon}
                              alt="Remix"
                              className="w-5 h-5"
                              style={{
                                filter:
                                  'invert(59%) sepia(79%) saturate(6039%) hue-rotate(167deg) brightness(109%) contrast(105%)'
                              }}
                            />
                          </button>
                        ) : (
                          <FeedRemixButton>
                            <LazyImage
                              src={remixButtonIcon}
                              alt="Remix"
                              style={{
                                width: 16,
                                height: 18
                              }}
                            />
                            <Text
                              fontSize={'14px'}
                              color="#FFFFFF"
                              fontWeight={600}
                              letterSpacing={'0.1px'}
                              style={{ minWidth: 0, flex: 1, overflow: 'hidden', display: 'inline-block' }}
                            >
                              {SUPER_APP_CONFIG.CREATE_CONTENT_DETAILS_ALT_REMIX}
                            </Text>
                          </FeedRemixButton>
                        )}
                      </div>
                    )}
                  </div>}
                </div>
              )
            })}
        </div>

        {isMobile && <MobileTabBar />}

        {/* Likes Bottom Sheet */}
         <LikeListBottomDrawer
          showLikesSheet={showLikesSheet}
          setShowLikesSheet={setShowLikesSheet}
          likesList={likesList}
          likesLoading={likesLoading}
          likesActionUserId={likesActionUserId}
          setShowGuestModal={setShowGuestModal}
          handleLikesSheetFollowToggle={handleLikesSheetFollowToggle}
          likesFollowState={likesFollowState}
        />

        {/* Comments Bottom Sheet */}
        <BottomSheetList
          open={showCommentsSheet}
          onClose={() => setShowCommentsSheet(false)}
          title="Comments"
          data={commentsList}
          searchPlaceholder="Search comments"
          renderItem={renderCommentItem}
          isCommentSheet={true}
          initialLoading={commentsLoading}
          commentInputValue={commentInput}
          setCommentInputValue={setCommentInput}
          onCommentSend={handleSendComment}
          commentPending={commentPending}
          onEndReached={fetchNextCommentsPage}
          hasNextPage={hasMoreComments}
          isFetchingNextPage={isFetchingMoreComments}
        />

        <ActionSheet
          open={showOptionsSheet}
          onClose={() => setShowOptionsSheet(false)}
          user={user}
          data={selectedPost}
          hideSave={false}
          onSaveToggle={handleToggleSave}
          onReportOpen={() => setReportOpen(true)}
        />

        <ReportSheet
          open={isReportOpen}
          onClose={() => setReportOpen(false)}
          reportReason={reportReason}
          setReportReason={setReportReason}
          reportText={reportText}
          setReportText={setReportText}
          user={user}
          contentData={selectedPost}
          onSubmit={() => {
            setReportOpen(false)
            setReportReason('')
            setReportText('')
          }}
          setReportedIds={setReportedIds}
        />

        {/* Remix Bottom Sheet */}
        <RemixBottomSheet
          open={showRemixSheet}
          onClose={() => setShowRemixSheet(false)}
          contentType="image"
          toolsData={popularToolsData.data}
          isLoading={popularToolsLoading}
          contentUrl={selectedPost?.file_url}
          isUGC={selectedPost?.is_ugc}
        />
        {showGuestModal ? (
          <GuestUserModal onClose={() => setShowGuestModal(false)} />
        ) : null}

        {<>{initialLoading && (
          Array.from({ length: 7 }).map((_, index) => (
            <CustomDiscoverCardShimmer key={index} />
          ))
        )}

          {isFetchingNextPage && (
            Array.from({ length: 7 }).map((_, index) => (
              <CustomDiscoverCardShimmer key={index} />
            ))
          )}

          {hasNextPage && (
            <div
              ref={loadMoreRef}
              style={{ height: '1px' }}
            />
          )}</>}
      </FeedContainer>
      <ShareModal
        openShareModal={isOpen}
        onClose={closeShare}
        shareData={shareDataDetails}
      />
       </ImageListWrapper>
  )
}

export default ImageList
